import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter 
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Plus, 
  Calendar, 
  MapPin, 
  Image as ImageIcon, 
  Video, 
  MoreHorizontal,
  Eye,
  Trash2,
  Edit,
  Upload,
  PartyPopper,
  Trophy,
  Palette,
  Music,
  Cake,
  GraduationCap,
  Dumbbell,
  Apple
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { FileUploadButton, AttachmentsList, DragDropUploader } from '../components/FileUpload';
import { toast } from 'sonner';

const EVENT_TYPES = [
  { value: 'sports_day', label: 'Sports Day', icon: Trophy },
  { value: 'sports_carnival', label: 'Sports Carnival', icon: Dumbbell },
  { value: 'fruits_festival', label: 'Fruits Festival', icon: Apple },
  { value: 'national_programme', label: 'National Programme', icon: PartyPopper },
  { value: 'cultural_programme', label: 'Cultural Programme', icon: Music },
  { value: 'art_competition', label: 'Art Competition', icon: Palette },
  { value: 'drawing_competition', label: 'Drawing Competition', icon: Palette },
  { value: 'birthday', label: 'Birthday Celebration', icon: Cake },
  { value: 'festival', label: 'Festival', icon: PartyPopper },
  { value: 'graduation', label: 'Graduation', icon: GraduationCap },
  { value: 'celebration', label: 'Celebration', icon: PartyPopper },
  { value: 'daily_activity', label: 'Daily Activity', icon: Calendar },
  { value: 'therapy_session', label: 'Therapy Session', icon: Calendar },
  { value: 'training', label: 'Training', icon: GraduationCap },
  { value: 'other', label: 'Other', icon: Calendar }
];

export default function EventsPage() {
  const { api, user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  
  const [formData, setFormData] = useState({
    name: '',
    event_type: 'daily_activity',
    date: '',
    description: '',
    location: '',
    is_public: false,
    notes: ''
  });

  useEffect(() => {
    fetchEvents();
  }, [typeFilter]);

  const fetchEvents = async () => {
    try {
      const params = {};
      if (typeFilter !== 'all') params.event_type = typeFilter;
      const response = await api.get('/events', { params });
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedEvent) {
        await api.put(`/events/${selectedEvent.id}`, formData);
        toast.success('Event updated');
      } else {
        await api.post('/events', formData);
        toast.success('Event created');
      }
      setDialogOpen(false);
      resetForm();
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save event');
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event and all its media?')) return;
    try {
      await api.delete(`/events/${eventId}`);
      toast.success('Event deleted');
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      event_type: 'daily_activity',
      date: '',
      description: '',
      location: '',
      is_public: false,
      notes: ''
    });
    setSelectedEvent(null);
  };

  const openEditDialog = (event) => {
    setSelectedEvent(event);
    setFormData({
      name: event.name,
      event_type: event.event_type,
      date: event.date,
      description: event.description || '',
      location: event.location || '',
      is_public: event.is_public,
      notes: event.notes || ''
    });
    setDialogOpen(true);
  };

  const openViewDialog = (event) => {
    setSelectedEvent(event);
    setViewDialogOpen(true);
  };

  const getEventTypeInfo = (type) => {
    return EVENT_TYPES.find(t => t.value === type) || EVENT_TYPES[EVENT_TYPES.length - 1];
  };

  const canManageEvents = ['director', 'admin', 'hr', 'therapist', 'clinical_lead'].includes(user?.role);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1e3a5f]">Events & Activities</h1>
          <p className="text-slate-500 mt-1">Manage events, celebrations and activities</p>
        </div>
        
        {canManageEvents && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90 rounded-full" data-testid="create-event-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Event
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{selectedEvent ? 'Edit Event' : 'Create New Event'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Event Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., Annual Sports Day 2026"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Event Type *</Label>
                    <Select 
                      value={formData.event_type} 
                      onValueChange={(v) => setFormData({...formData, event_type: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {EVENT_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Date *</Label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({...formData, date: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    placeholder="e.g., Main Hall, Outdoor Area"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={3}
                    placeholder="Describe the event..."
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_public"
                    checked={formData.is_public}
                    onChange={(e) => setFormData({...formData, is_public: e.target.checked})}
                  />
                  <Label htmlFor="is_public" className="cursor-pointer">
                    Visible to parents (public event)
                  </Label>
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-[#1e3a5f]">
                    {selectedEvent ? 'Update Event' : 'Create Event'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <Card className="card-icrcl">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Label className="text-slate-600">Filter by type:</Label>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Events</SelectItem>
                {EVENT_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Events Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-4 border-[#1e3a5f] border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      ) : events.length === 0 ? (
        <Card className="card-icrcl">
          <CardContent className="text-center py-12">
            <PartyPopper className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-600">No Events Yet</h3>
            <p className="text-slate-500 mb-4">Create your first event to get started</p>
            {canManageEvents && (
              <Button onClick={() => setDialogOpen(true)} className="bg-[#1e3a5f]">
                <Plus className="w-4 h-4 mr-2" />
                Create Event
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((event) => {
            const typeInfo = getEventTypeInfo(event.event_type);
            const IconComponent = typeInfo.icon;
            
            return (
              <Card key={event.id} className="card-icrcl overflow-hidden hover:shadow-lg transition-shadow">
                {/* Featured Image or Placeholder */}
                <div className="h-40 bg-gradient-to-br from-[#1e3a5f] to-[#2a9d8f] relative">
                  {event.featured_image ? (
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}${event.featured_image}`} 
                      alt={event.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <IconComponent className="w-16 h-16 text-white/50" />
                    </div>
                  )}
                  <Badge className="absolute top-2 left-2 bg-white/90 text-[#1e3a5f]">
                    {typeInfo.label}
                  </Badge>
                  {event.attachment_count > 0 && (
                    <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-black/50 text-white px-2 py-1 rounded text-xs">
                      <ImageIcon className="w-3 h-3" />
                      {event.attachment_count}
                    </div>
                  )}
                </div>

                <CardContent className="p-4">
                  <h3 className="font-semibold text-lg text-[#1e3a5f] mb-2">{event.name}</h3>
                  
                  <div className="space-y-1 text-sm text-slate-600 mb-3">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(event.date).toLocaleDateString('en-GB', { 
                        weekday: 'short', 
                        day: 'numeric', 
                        month: 'short', 
                        year: 'numeric' 
                      })}</span>
                    </div>
                    {event.location && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span>{event.location}</span>
                      </div>
                    )}
                  </div>

                  {event.description && (
                    <p className="text-sm text-slate-500 line-clamp-2 mb-3">{event.description}</p>
                  )}

                  <div className="flex items-center justify-between pt-2 border-t">
                    <span className="text-xs text-slate-400">
                      By {event.created_by_name}
                    </span>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openViewDialog(event)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      {canManageEvents && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(event)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(event.id)} className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* View Event Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedEvent && (
            <>
              <DialogHeader>
                <DialogTitle className="text-[#1e3a5f]">{selectedEvent.name}</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2 text-sm">
                  <Badge variant="outline">
                    {getEventTypeInfo(selectedEvent.event_type).label}
                  </Badge>
                  <Badge variant="outline">
                    <Calendar className="w-3 h-3 mr-1" />
                    {new Date(selectedEvent.date).toLocaleDateString('en-GB')}
                  </Badge>
                  {selectedEvent.location && (
                    <Badge variant="outline">
                      <MapPin className="w-3 h-3 mr-1" />
                      {selectedEvent.location}
                    </Badge>
                  )}
                </div>

                {selectedEvent.description && (
                  <p className="text-slate-600">{selectedEvent.description}</p>
                )}

                {/* Upload Media */}
                {canManageEvents && (
                  <div className="space-y-4">
                    <DragDropUploader
                      entityType="event"
                      entityId={selectedEvent.id}
                      onUploadComplete={fetchEvents}
                      acceptTypes="media"
                      multiple={true}
                      title="Upload Event Photos & Videos"
                      description="Drag & drop images and videos here or click to browse"
                    />
                  </div>
                )}

                {/* Attachments */}
                <AttachmentsList
                  entityType="event"
                  entityId={selectedEvent.id}
                  title="Event Photos & Videos"
                  showUploadButton={false}
                />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
